import { useMemo } from 'react'
import { useIsArticleBeingAdded } from 'components/WorkspaceEditor/features/createNewContent'
import { useShallowSelector } from 'components/WorkspaceEditor/Workspace2Store'
import { DataNode } from 'components/WorkspaceEditor/types'
import { PROPERTY_EDITOR_EDITABLE_TEXT_VARIANT as NODE_TYPES } from 'pages/ProjectsPage/WorkspacePage/components/constants'

/**
 * Hook that determines if a specific node is currently being added and not yet saved.
 * Used to disable drag and visibility icons for unsaved content.
 *
 * @param node - The tree node to check
 * @returns Boolean indicating if this node is being added
 */
export const useIsNodeBeingAdded = (node: DataNode): boolean => {
    const isThisArticleBeingAdded = useIsArticleBeingAdded(node)
    const { textContentElementToAdd } = useShallowSelector((state) => ({
        textContentElementToAdd: state.textContentElementToAdd,
    }))

    return useMemo(() => {
        if (node.type === NODE_TYPES.Article && isThisArticleBeingAdded) {
            return true
        }

        if (node.type === NODE_TYPES.Paragraph) {
            if (textContentElementToAdd && node.key === textContentElementToAdd.id) {
                return true
            }

            if (node.contentItemNodes && node.contentItemNodes.length > 0) {
                return node.contentItemNodes.some(
                    (element) => element && element.changeType === 'ADDED' && (!element.text || element.text.trim() === '')
                )
            }
        }

        return false
    }, [node, isThisArticleBeingAdded, textContentElementToAdd])
}